import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import Estado from '../components/ui/Estado';
import Select from '../components/Select';
import ItemCard from '../components/ItemCard';
import ModalConfirm from '../components/ModalConfirm';
import Modal from '../components/ui/Modal';
import { toast } from '../components/Toast';
import {
  labelUnidade, labelUnidadePlural, formatarProducao, UNIDADES_PRODUCAO,
} from '../utils/unidades';
import Field, { FormRow } from '../components/ui/Field';
import DataTable from '../components/ui/DataTable';
import EmptyState from '../components/ui/EmptyState';
import PageHeader from '../components/ui/PageHeader';
import Metrica, { MetricaGrid } from '../components/ui/Metrica';
import { fmt, fmtBRL, fmtData, hoje as hojeISO } from '../utils/format';

const hoje = hojeISO;
const fmtNum = fmt;

const formVazio = { armazemId: '', safraId: '', dataVenda: hoje(), quantidade: '', precoUnidade: '', unidadeVenda: 'KG', observacao: '', vendidoEm: '' };

export default function Vendas() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // Lista primeiro: quem abre Vendas quase sempre quer conferir o que já vendeu.
  // O formulário é uma ação deliberada, pelo botão do cabeçalho.
  const [aba, setAba] = useState(() => (params.get('aba') === 'estoque' ? 'estoque' : 'lista'));
  const [armazens, setArmazens] = useState([]);
  const [safras, setSafras] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [vendas, setVendas] = useState([]);
  const [form, setForm] = useState(formVazio);
  const [formAberto, setFormAberto] = useState(false);
  const [editando, setEditando] = useState(null);
  const [excluindo, setExcluindo] = useState(null);
  const [msg, setMsg] = useState(null);
  // Unidades que a safra aceita em venda (grandeza peso já expandida pelo backend)
  // e a marca de safra sem nenhuma carga registrada.
  const [unidadesVendaveis, setUnidadesVendaveis] = useState([]);
  const [safraSemProducao, setSafraSemProducao] = useState(false);
  const [carregandoLista, setCarregandoLista] = useState(false);
  const [erroLista, setErroLista] = useState('');
  const [filtroSafraLista, setFiltroSafraLista] = useState('');
  const [buscandoEstoque, setBuscandoEstoque] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erroCombos, setErroCombos] = useState('');
  /** Prefill sugerido para conta a receber após registrar venda nova. */
  const [ofertaContaReceber, setOfertaContaReceber] = useState(null);
  // Relatório estoque
  const [filtroArmazemId, setFiltroArmazemId] = useState('');
  const [filtroSafraId, setFiltroSafraId] = useState('');
  const [estoque, setEstoque] = useState(null);
  const [filtroAno, setFiltroAno] = useState('');

  // Menu "Estoque de produção" entra com ?aba=estoque
  useEffect(() => {
    if (params.get('aba') === 'estoque') setAba('estoque');
  }, [params]);

  // Abrir uma venda para editar dispara a carga de unidades pela troca de safraId.
  // Sem esta marca, a normalização trocava a unidade do registro (100 caixas viravam
  // 100 kg ao salvar). Vale uma vez só: se depois o usuário trocar a safra à mão, a
  // normalização volta a valer.
  const preservarUnidadeRef = useRef(false);

  // Antes o erro era engolido: com a rede caída os combos ficavam vazios e
  // parecia que não havia safra cadastrada.
  const carregarCombos = useCallback(async () => {
    setErroCombos('');
    try {
      const [arm, saf] = await Promise.all([api.getArmazens(), api.getSafras()]);
      setArmazens(arm || []);
      setSafras(saf || []);
    } catch (err) {
      setErroCombos(err.message || 'Não foi possível carregar as safras e os armazéns.');
    }
    api.getUsuarios().then(setUsuarios).catch(() => setUsuarios([]));
  }, []);

  useEffect(() => { carregarCombos(); }, [carregarCombos]);

  // Lista vazia significa safra sem produção; lista preenchida traz as unidades
  // vendáveis já expandidas, com a mais produzida na frente.
  useEffect(() => {
    if (!form.safraId) { setUnidadesVendaveis([]); setSafraSemProducao(false); return; }
    let vivo = true;
    api.getUnidadesSafra(form.safraId)
      .then(u => {
        if (!vivo) return;
        const lista = u || [];
        setUnidadesVendaveis(lista);
        setSafraSemProducao(lista.length === 0);
        // Edição preserva a unidade gravada, mesmo que a safra não a produza mais:
        // o tratamento de "registro anterior" mais abaixo cuida de exibi-la.
        if (preservarUnidadeRef.current) {
          preservarUnidadeRef.current = false;
          return;
        }
        // Pré-seleciona a primeira unidade e corrige a escolha ao trocar de safra.
        // A comparação vai dentro do updater para o efeito não depender da unidade atual.
        if (lista.length > 0) {
          setForm(f => (lista.includes(f.unidadeVenda) ? f : { ...f, unidadeVenda: lista[0] }));
        }
      })
      .catch(() => {
        // Consome a marca também na falha, para não vazar para a próxima carga.
        preservarUnidadeRef.current = false;
        if (vivo) { setUnidadesVendaveis([]); setSafraSemProducao(false); }
      });
    return () => { vivo = false; };
  }, [form.safraId]);

  // Antes a falha era engolida e a lista ficava vazia sem explicação.
  const carregarVendas = useCallback(async () => {
    setCarregandoLista(true);
    setErroLista('');
    try {
      setVendas(await api.listarVendas() || []);
    } catch (err) {
      setErroLista(err.message || 'Não foi possível carregar as vendas.');
    } finally {
      setCarregandoLista(false);
    }
  }, []);

  useEffect(() => {
    if (aba === 'lista') carregarVendas();
  }, [aba, carregarVendas]);

  const salvar = async (e) => {
    e.preventDefault();
    // Enter no formulário também submete, então o `disabled` do botão não basta
    // para evitar a venda duplicada.
    if (salvando) return;
    setMsg(null);
    // O Select de safra não bloqueia o submit pelo navegador: sem esta checagem o
    // botão simplesmente não respondia.
    if (!form.safraId) { setMsg({ type: 'error', text: 'Escolha a safra.' }); return; }
    setSalvando(true);
    try {
      const qtd = parseFloat(form.quantidade);
      const preco = parseFloat(form.precoUnidade);
      const payload = {
        armazemId: form.armazemId ? parseInt(form.armazemId) : null,
        safraId: parseInt(form.safraId),
        dataVenda: form.dataVenda,
        quantidade: qtd,
        precoUnidade: preco,
        unidadeVenda: form.unidadeVenda || 'KG',
        quantidadeKg: form.unidadeVenda === 'KG' ? qtd : null,
        precoSacoBRL: form.unidadeVenda === 'SACO' ? preco : null,
        observacao: form.observacao || null,
        vendidoEm: form.vendidoEm || null,
      };
      if (editando) {
        await api.atualizarVenda(editando, payload);
        toast('Venda atualizada.', 'success');
        preservarUnidadeRef.current = false;
        setEditando(null);
        setForm(formVazio);
        setFormAberto(false);
        carregarVendas();
      } else {
        await api.criarVenda(payload);
        const valorTotal = Number((qtd * preco).toFixed(2));
        const armazem = armazens.find((a) => String(a.id) === String(form.armazemId));
        const pagador = armazem
          ? [armazem.nome, armazem.cidade].filter(Boolean).join(' — ')
          : '';
        const descricao = form.observacao?.trim()
          || `Venda de ${fmtData(form.dataVenda)}`;
        toast('Venda registrada.', 'success');
        preservarUnidadeRef.current = false;
        setForm(formVazio);
        setFormAberto(false);
        carregarVendas();
        setOfertaContaReceber({
          valor: String(valorTotal),
          pagador,
          descricao,
        });
      }
    } catch (err) { setMsg({ type: 'error', text: err.message }); }
    finally { setSalvando(false); }
  };

  const iniciarEdicao = (v) => {
    // Marca antes do setForm: o efeito de unidades dispara pela troca de safraId.
    preservarUnidadeRef.current = true;
    setForm({
      armazemId: String(v.armazem?.id || ''),
      safraId: String(v.safra?.id || ''),
      dataVenda: v.dataVenda,
      quantidade: v.quantidade || v.quantidadeKg || '',
      precoUnidade: v.precoUnidade || v.precoSacoBRL || '',
      unidadeVenda: v.unidadeVenda || 'SACO',
      observacao: v.observacao || '',
      vendidoEm: v.vendidoEm || ''
    });
    setEditando(v.id);
    setFormAberto(true);
  };

  const excluir = async (id) => {
    try { await api.excluirVenda(id); setExcluindo(null); toast('Venda excluída.', 'success'); carregarVendas(); }
    catch (err) { toast(err.message, 'error'); }
  };

  const buscarEstoque = async () => {
    if (!filtroSafraId || buscandoEstoque) return;
    setBuscandoEstoque(true);
    try { setEstoque(await api.getEstoqueArmazem(filtroArmazemId || null, filtroSafraId)); }
    catch (err) { toast(err.message, 'error'); }
    finally { setBuscandoEstoque(false); }
  };

  // Safra sem produção libera todas as unidades; com produção, só as vendáveis.
  const unidadesBase = safraSemProducao ? UNIDADES_PRODUCAO.map(u => u.value) : unidadesVendaveis;
  // Venda antiga em unidade que hoje não é vendável continua listada e marcada,
  // para o produtor corrigir data, preço ou quantidade sem trocar a unidade.
  const unidadeLegada = editando && form.unidadeVenda && !unidadesBase.includes(form.unidadeVenda)
    ? form.unidadeVenda
    : null;
  const opcoesUnidade = [...unidadesBase, ...(unidadeLegada ? [unidadeLegada] : [])].map(v => ({
    value: v,
    label: v === unidadeLegada ? `${labelUnidade(v)} — registro anterior` : labelUnidade(v),
  }));
  const avisoUnidadeLegada = !!unidadeLegada && unidadesVendaveis.length > 0;

  const hintUnidade = () => {
    if (!form.safraId) return 'Escolha a safra primeiro';
    if (safraSemProducao) return 'Esta safra ainda não tem produção registrada.';
    if (unidadesVendaveis.includes('KG') && unidadesVendaveis.includes('SACO')) {
      return '1 saco = 60 kg, então você pode vender nas duas unidades.';
    }
    if (unidadesVendaveis.length === 1) {
      return 'A venda é registrada na unidade em que você registrou a produção.';
    }
    return undefined;
  };

  const anosDisponiveis = [...new Set(vendas.map(v => v.dataVenda?.slice(0, 4)).filter(Boolean))].sort((a, b) => b - a);
  const vendasFiltradas = vendas
    .filter(v => !filtroAno || v.dataVenda?.startsWith(filtroAno))
    .filter(v => !filtroSafraLista || String(v.safra?.id) === filtroSafraLista);
  const receitaFiltrada = vendasFiltradas.reduce(
    (s, v) => s + ((v.quantidade ?? v.quantidadeKg ?? 0) * (v.precoUnidade ?? v.precoSacoBRL ?? 0)), 0);

  const abrirNovaVenda = () => {
    preservarUnidadeRef.current = false;
    setEditando(null);
    setForm(formVazio);
    setMsg(null);
    setFormAberto(true);
  };

  const fecharFormVenda = () => {
    preservarUnidadeRef.current = false;
    setEditando(null);
    setForm(formVazio);
    setMsg(null);
    setFormAberto(false);
  };

  return (
    <div>
      <PageHeader
        titulo="Vendas"
        icone="venda"
        descricao="O que saiu da fazenda, a receita e o saldo que ainda resta para vender."
        acao={{
          label: 'Registrar venda',
          icone: 'adicionar',
          onClick: abrirNovaVenda,
        }}
      />

      <div className="tabs" role="tablist">
        <button role="tab" aria-selected={aba === 'lista'} className={`tab ${aba === 'lista' ? 'active' : ''}`}
          onClick={() => setAba('lista')}>Vendas registradas</button>
        <button role="tab" aria-selected={aba === 'estoque'} className={`tab ${aba === 'estoque' ? 'active' : ''}`}
          onClick={() => setAba('estoque')}>Estoque</button>
      </div>

      {/* Fica fora das abas porque safra e armazém alimentam o formulário, os
          filtros da lista e a consulta de estoque. */}
      {erroCombos && (
        <div className="aviso aviso--erro">
          {erroCombos}{' '}
          <button type="button" className="link-inline" onClick={carregarCombos}>
            Tentar novamente
          </button>
        </div>
      )}

      {formAberto && (
        <Modal
          titulo={editando ? 'Editar venda' : 'Registrar venda'}
          onClose={fecharFormVenda}
          largura={620}
          bloqueado={salvando}
        >
          {msg && <div className={`msg msg-${msg.type}`}>{msg.text}</div>}
          <form onSubmit={salvar}>
            <FormRow>
              <Field label="Data da venda" obrigatorio>
                <input type="date" value={form.dataVenda} onChange={e => setForm({ ...form, dataVenda: e.target.value })} required />
              </Field>
              <Field label="Safra / Ciclo" obrigatorio>
                <Select value={form.safraId}
                  onChange={v => { preservarUnidadeRef.current = false; setForm({ ...form, safraId: v }); }}
                  options={safras.map(s => ({ value: s.id, label: `${s.cultura || 'Safra'} - ${s.descricao}` }))}
                  placeholder="Selecione" required />
              </Field>
            </FormRow>

            <FormRow>
              <Field label="Unidade" obrigatorio hint={hintUnidade()}>
                <Select value={form.unidadeVenda} onChange={v => setForm({ ...form, unidadeVenda: v })}
                  options={opcoesUnidade}
                  placeholder="Selecione"
                  disabled={!form.safraId} />
              </Field>
              <Field label={`Quantidade (${labelUnidadePlural(form.unidadeVenda)})`} obrigatorio>
                <input type="number" step="0.01" min="0" value={form.quantidade}
                  onChange={e => setForm({ ...form, quantidade: e.target.value })} required placeholder="Ex: 500" />
              </Field>
              <Field label={`Preço por ${labelUnidade(form.unidadeVenda)}`} obrigatorio>
                <input type="number" step="0.01" min="0" value={form.precoUnidade}
                  onChange={e => setForm({ ...form, precoUnidade: e.target.value })} required placeholder="Ex: 150,00" />
              </Field>
            </FormRow>

            <FormRow>
              <Field label="Armazém" hint="Opcional — de onde saiu a mercadoria">
                <Select value={form.armazemId} onChange={v => setForm({ ...form, armazemId: v })}
                  options={armazens.map(a => ({ value: a.id, label: `${a.nome} - ${a.cidade}` }))}
                  placeholder="Não informado" />
              </Field>
              <Field label="Vendido em nome de" hint="Opcional — ajuda a separar por CPF no imposto de renda">
                <Select value={form.vendidoEm} onChange={v => setForm({ ...form, vendidoEm: v })}
                  options={usuarios.map(u => ({ value: u.nome, label: u.nome }))}
                  placeholder="Não informado" />
              </Field>
              <Field label="Observação" hint="Opcional — comprador, nota fiscal, condição">
                <input value={form.observacao} onChange={e => setForm({ ...form, observacao: e.target.value })}
                  placeholder="Ex: Cooperativa, NF 1234" />
              </Field>
            </FormRow>
            {/* Venda antiga em unidade que a safra não produziu: pode ser salva como está. */}
            {avisoUnidadeLegada && (
              <div className="aviso aviso--atencao" style={{ margin: '1rem 0' }}>
                A unidade desta venda (<strong>{labelUnidadePlural(form.unidadeVenda)}</strong>) não
                corresponde à produção registrada na safra, que foi em{' '}
                <strong>{unidadesVendaveis.map(labelUnidadePlural).join(', ')}</strong>. Você pode
                corrigir data, quantidade e preço mantendo a unidade do registro anterior.
              </div>
            )}

            {form.quantidade && form.precoUnidade && (
              <div className="aviso aviso--ok">
                <strong>Total da venda: {fmtBRL(parseFloat(form.quantidade) * parseFloat(form.precoUnidade))}</strong>
                <span style={{ opacity: 0.85 }}>
                  {' '}({fmt(parseFloat(form.quantidade))} {labelUnidadePlural(form.unidadeVenda)}
                  {' '}× {fmtBRL(parseFloat(form.precoUnidade))})
                </span>
              </div>
            )}
            <div className="actions modal-rodape">
              <button type="button" className="btn-secondary" disabled={salvando} onClick={fecharFormVenda}>
                Cancelar
              </button>
              <button type="submit" className="btn-primary" disabled={salvando}>
                {salvando
                  ? (editando ? 'Salvando...' : 'Registrando...')
                  : (editando ? 'Salvar alterações' : 'Registrar venda')}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* LISTA */}
      {aba === 'lista' && (
        <div className="card">
          <div className="secao-titulo">
            <h2>Vendas Registradas</h2>
            <span className="contador">
              {vendasFiltradas.length}{filtroAno || filtroSafraLista ? ` de ${vendas.length}` : ''}
            </span>
          </div>

          {/* Antes só havia filtro por ano. Safra é o recorte que o produtor mais
              usa, porque é como ele pensa a produção. */}
          {vendas.length > 0 && (
            <div className="filtros">
              <Field label="Ano">
                <Select value={filtroAno} onChange={setFiltroAno}
                  options={anosDisponiveis.map(a => ({ value: a, label: a }))}
                  placeholder="Todos os anos" />
              </Field>
              <Field label="Safra / Ciclo">
                <Select value={filtroSafraLista} onChange={setFiltroSafraLista}
                  options={safras.map(s => ({ value: String(s.id), label: `${s.cultura || 'Safra'} - ${s.descricao}` }))}
                  placeholder="Todas as safras" />
              </Field>
              {(filtroAno || filtroSafraLista) && (
                <button type="button" className="btn-secondary"
                  onClick={() => { setFiltroAno(''); setFiltroSafraLista(''); }}>
                  Limpar
                </button>
              )}
            </div>
          )}

          <Estado
            carregando={carregandoLista}
            erro={erroLista}
            aoTentarNovamente={carregarVendas}
            vazio={vendasFiltradas.length === 0}
            vazioProps={vendas.length === 0
              ? {
                  icone: 'venda',
                  titulo: 'Nenhuma venda registrada',
                  descricao: 'Registre sua primeira venda para acompanhar receita e saldo de estoque.',
                  acao: { onClick: abrirNovaVenda, label: 'Registrar venda' },
                }
              : {
                  icone: 'busca',
                  titulo: 'Nenhuma venda com esses filtros',
                  descricao: 'Ajuste o ano ou a safra para ver outros resultados.',
                }}
          >
            <>
              {/* Resumo do que está filtrado: responde "quanto vendi?" sem somar na mão. */}
              <MetricaGrid min={140}>
                <Metrica label="Vendas" value={vendasFiltradas.length} />
                <Metrica label="Receita total" value={fmtBRL(receitaFiltrada)} cor="#276749" destaque />
                <Metrica label="Ticket médio" value={fmtBRL(receitaFiltrada / vendasFiltradas.length)} />
              </MetricaGrid>

              <div style={{ marginTop: '1.25rem' }}>
                {vendasFiltradas.map(v => {
                  const u = v.unidadeVenda || 'SACO';
                  const qtd = v.quantidade ?? v.quantidadeKg;
                  const preco = v.precoUnidade ?? v.precoSacoBRL;
                  const total = qtd && preco ? qtd * preco : 0;
                  return (
                    <ItemCard key={v.id} onEdit={() => iniciarEdicao(v)} onDelete={() => setExcluindo(v)}>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', minWidth: 72 }}>
                        {fmtData(v.dataVenda)}
                      </span>
                      <span style={{ flex: 1, fontWeight: 600, minWidth: 120 }}>
                        {v.safra?.cultura || 'Safra'} - {v.safra?.descricao}
                        {v.armazem && (
                          <span style={{ display: 'block', fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 400 }}>
                            {v.armazem.nome}
                          </span>
                        )}
                      </span>
                      <span style={{ fontSize: '0.82rem' }}>
                        <strong>{fmt(qtd)}</strong>{' '}
                        <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>{labelUnidadePlural(u)}</span>
                      </span>
                      <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                        {fmtBRL(preco)}/{labelUnidade(u)}
                      </span>
                      <span style={{ fontSize: '0.9rem', fontWeight: 700, color: '#276749' }}>
                        {fmtBRL(total)}
                      </span>
                    </ItemCard>
                  );
                })}
              </div>
            </>
          </Estado>
        </div>
      )}

      {/* ESTOQUE */}
      {aba === 'estoque' && (
        <>
          <div className="card">
            <h2>Estoque de Produção</h2>
            <p className="secao-sub">
              Mostra quanto ainda resta para vender: tudo o que você registrou como
              produção, menos o que já saiu em vendas. O saldo é separado por unidade,
              porque caixas e quilos não se somam.
            </p>
            <div className="filtros">
              <Field label="Safra / Ciclo" obrigatorio>
                <Select value={filtroSafraId} onChange={v => { setFiltroSafraId(v); setEstoque(null); }}
                  options={safras.map(s => ({ value: s.id, label: `${s.cultura || 'Safra'} - ${s.descricao}` }))}
                  placeholder="Selecione a safra" />
              </Field>
              {estoque && (
                <Field label="Armazém">
                  <Select value={filtroArmazemId} onChange={setFiltroArmazemId}
                    options={[{ value: '0', label: 'Não informado' }, ...armazens.map(a => ({ value: a.id, label: `${a.nome} - ${a.cidade}` }))]}
                    placeholder="Todos os armazéns" />
                </Field>
              )}
              <button className="btn-primary" onClick={buscarEstoque} disabled={!filtroSafraId || buscandoEstoque}>
                {buscandoEstoque ? 'Consultando...' : estoque ? 'Atualizar' : 'Consultar'}
              </button>
            </div>

            {!estoque && !buscandoEstoque && (
              <EmptyState
                compacto
                icone="estoque"
                titulo="Escolha uma safra"
                descricao="Selecione a safra e clique em Consultar para ver o saldo disponível."
              />
            )}
          </div>

          {estoque && (
            <>
              <div className="card">
                <h2>Resumo — {estoque.nomeArmazem} / Safra {estoque.descricaoSafra}</h2>

                {estoque.unidadesMistas && (
                  <div className="aviso aviso--atencao" style={{ margin: '0.5rem 0 1rem' }}>
                    <strong>Produção em unidades diferentes.</strong> Cada unidade tem seu
                    próprio saldo — caixas e quilos não se somam.
                  </div>
                )}

                {/* Um bloco de saldo por unidade. */}
                {estoque.saldosPorUnidade?.length > 0 ? estoque.saldosPorUnidade.map(s => (
                  <div key={s.unidade} style={{ marginBottom: '1rem' }}>
                    {estoque.saldosPorUnidade.length > 1 && (
                      <h3 style={{ fontSize: '0.8rem', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {labelUnidadePlural(s.unidade)}
                      </h3>
                    )}
                    <div className="form-grid">
                      <div style={{ background: '#e8f5e9', padding: '1rem', borderRadius: 8 }}>
                        <div style={{ fontSize: '0.8rem', color: '#555' }}>Total Produzido</div>
                        <div style={{ fontSize: '1.3rem', fontWeight: 700 }}>{formatarProducao(s.produzido, s.unidade)}</div>
                        {s.produzidoSacos != null && (
                          <div style={{ fontSize: '0.9rem', color: '#2e7d32' }}>{fmtNum(s.produzidoSacos)} sacos</div>
                        )}
                      </div>
                      <div style={{ background: '#fff3e0', padding: '1rem', borderRadius: 8 }}>
                        <div style={{ fontSize: '0.8rem', color: '#555' }}>Total Vendido</div>
                        <div style={{ fontSize: '1.3rem', fontWeight: 700 }}>{formatarProducao(s.vendido, s.unidade)}</div>
                        {s.vendidoSacos != null && (
                          <div style={{ fontSize: '0.9rem', color: '#e65100' }}>{fmtNum(s.vendidoSacos)} sacos</div>
                        )}
                      </div>
                      <div style={{ background: s.saldo < 0 ? '#fff5f5' : '#e3f2fd', padding: '1rem', borderRadius: 8 }}>
                        <div style={{ fontSize: '0.8rem', color: '#555' }}>Saldo Disponível</div>
                        <div style={{ fontSize: '1.3rem', fontWeight: 700, color: s.saldo < 0 ? '#c53030' : undefined }}>
                          {formatarProducao(s.saldo, s.unidade)}
                        </div>
                        {s.saldoSacos != null && (
                          <div style={{ fontSize: '0.9rem', color: s.saldo < 0 ? '#c53030' : '#1565c0' }}>{fmtNum(s.saldoSacos)} sacos</div>
                        )}
                        {s.saldo < 0 && (
                          <div style={{ fontSize: '0.72rem', color: '#c53030', marginTop: '0.2rem' }}>
                            vendido acima do produzido
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )) : (
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', padding: '0.5rem 0' }}>
                    Nenhuma produção ou venda registrada nesta safra.
                  </p>
                )}
              </div>

              {estoque.vendas?.length > 0 && (
                <div className="card">
                  <div className="secao-titulo">
                    <h2>Vendas desta safra</h2>
                    <span className="contador">{estoque.vendas.length} venda{estoque.vendas.length === 1 ? '' : 's'}</span>
                  </div>
                  <DataTable
                    densa
                    linhas={estoque.vendas}
                    chaveDe={v => v.id}
                    colunas={[
                      // No cartão a quantidade vendida identifica a venda ("500
                      // sacos") melhor que a data, e o total é o destaque.
                      { chave: 'qtd', titulo: 'Quantidade', alinhar: 'direita', papel: 'titulo', render: v => {
                          const u = v.unidadeVenda || 'SACO';
                          return `${fmt(v.quantidade ?? v.quantidadeKg)} ${labelUnidadePlural(u)}`;
                        } },
                      { chave: 'total', titulo: 'Total', alinhar: 'direita', papel: 'destaque',
                        render: v => fmtBRL((v.quantidade ?? v.quantidadeKg ?? 0) * (v.precoUnidade ?? v.precoSacoBRL ?? 0)) },
                      { chave: 'data', titulo: 'Data', papel: 'subtitulo', render: v => fmtData(v.dataVenda) },
                      { chave: 'preco', titulo: 'Preço unit.', alinhar: 'direita',
                        render: v => fmtBRL(v.precoUnidade ?? v.precoSacoBRL) },
                      { chave: 'obs', titulo: 'Observação', ocultarSeVazia: true,
                        valor: v => v.observacao, render: v => v.observacao || '' },
                    ]}
                    // Posicional, na ordem das colunas acima: qtd, total, data,
                    // preço, obs. Reordenar as colunas exige mexer aqui também.
                    rodape={[
                      'Total',
                      fmtBRL(estoque.vendas.reduce((s, v) =>
                        s + ((v.quantidade ?? v.quantidadeKg ?? 0) * (v.precoUnidade ?? v.precoSacoBRL ?? 0)), 0)),
                      '',
                      '',
                      '',
                    ]}
                  />
                </div>
              )}
            </>
          )}
        </>
      )}

      {excluindo && (
        <ModalConfirm
          titulo="Excluir Venda"
          mensagem={<>Tem certeza que deseja excluir a venda de <strong>{excluindo.armazem?.nome}</strong> — Safra <strong>{excluindo.safra?.descricao}</strong>?</>}
          onConfirm={() => excluir(excluindo.id)}
          onCancel={() => setExcluindo(null)}
        />
      )}

      {ofertaContaReceber && (
        <Modal titulo="Conta a receber?" onClose={() => setOfertaContaReceber(null)} largura={440}>
          <p style={{ margin: 0, color: 'var(--text)', lineHeight: 1.6, fontSize: '0.9rem' }}>
            Esta venda será recebida depois? Você pode registrar uma conta a receber
            com o valor já preenchido
            {ofertaContaReceber.pagador ? ' e o armazém como quem vai pagar' : ''}.
          </p>
          <p style={{ marginTop: '0.75rem', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
            Valor: <strong style={{ color: 'var(--text)' }}>{fmtBRL(parseFloat(ofertaContaReceber.valor))}</strong>
            {ofertaContaReceber.pagador ? (
              <> · De quem: <strong style={{ color: 'var(--text)' }}>{ofertaContaReceber.pagador}</strong></>
            ) : null}
          </p>
          <div className="actions modal-rodape">
            <button type="button" className="btn-secondary" onClick={() => setOfertaContaReceber(null)}>
              Não
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => {
                const q = new URLSearchParams();
                if (ofertaContaReceber.valor) q.set('valor', ofertaContaReceber.valor);
                if (ofertaContaReceber.pagador) q.set('pagador', ofertaContaReceber.pagador);
                if (ofertaContaReceber.descricao) q.set('descricao', ofertaContaReceber.descricao);
                setOfertaContaReceber(null);
                navigate(`/contas-a-receber?${q.toString()}`);
              }}
            >
              Sim, registrar
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
